import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import type { RpaDocumentsSchemaDataItemErrorSummary } from '@liscio/api';
import { useGetApiV5FirmsRpaDocuments } from '@liscio/api';
import { useApiClient, useTrackEvent } from '@liscio/common';
import { Box, Icon, Stack, Typography } from '@liscio/ui';

import {
  WHAT_TO_DO_TODAY_COMING_FROM,
  WHAT_TO_DO_TODAY_DASHBOARD_STEPS,
  WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM_V2,
  WHAT_TO_DO_TODAY_SOURCE,
} from '../constants';
import { fetchSamplePdf } from '../sampleDocuments';
import { config } from 'config';
import { useProfileData } from 'fetch-utils/users/user-hooks';
import { SmartTaxDeliveryUploadCard } from 'modules/smart-tax/components/SmartTaxImport/shared/SmartTaxDeliveryUploadCard';
import type {
  FileWithRpaType,
  WorkflowRPARequestPayload,
  WorkflowRPARequestResponse,
} from 'modules/smart-tax/components/SmartTaxUpload/SmartTaxUpload';
import {
  FileError,
  useActiveStorageFileUploader,
} from 'modules/smart-tax/hooks/useActiveStorageFileUploader';

const TAX_DELIVERY_RPA_TYPE = 'tax_delivery';
const MAX_FILES = 1;
const POLLING_INTERVAL_MS = 3_000;
const WORKFLOW_REQUEST_WAIT_TIMEOUT_MS = 60_000;

const TAX_DELIVERY_SAMPLES = [
  {
    label: 'Sample return 1',
    filename: '2025_Tax_Return_Sample_1.pdf',
    path: '/samples/smart-tax-delivery/2025_Tax_Return_Sample_1.pdf',
  },
  {
    label: 'Sample return 2',
    filename: '2025_Tax_Return_Sample_2.pdf',
    path: '/samples/smart-tax-delivery/2025_Tax_Return_Sample_2.pdf',
  },
  {
    label: 'Sample return 3',
    filename: '2025_Tax_Return_Sample_3.pdf',
    path: '/samples/smart-tax-delivery/2025_Tax_Return_Sample_3.pdf',
  },
] as const;

type ProcessingStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'failed';

export interface SmartTaxDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (result: SmartTaxDeliveryCompletionResult) => void;
}

export interface SmartTaxDeliveryCompletionResult {
  wfRequestUuid: string;
  rpaDocumentUuid: string | null;
  fileCount: number;
}

function addTaxDeliveryRpaType(file: File): FileWithRpaType {
  const fileWithRpaType = file as FileWithRpaType;
  fileWithRpaType.rpa_type = TAX_DELIVERY_RPA_TYPE;
  return fileWithRpaType;
}

function isFileError(file: ActiveStorage.Blob | FileError): file is FileError {
  return (file as FileError).error === true;
}

function useSmartTaxDeliveryUpload(
  isActive: boolean,
  onComplete: (result: SmartTaxDeliveryCompletionResult) => void
) {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { trackEvent } = useTrackEvent();
  const { cpa_user_id } = useProfileData();
  const dedupeListRef = useRef<FileWithRpaType[]>([]);
  const hasProcessedUploadRef = useRef(false);
  const completionFiredRef = useRef(false);
  const processingFileCountRef = useRef(0);
  const workflowRequestWaitStartedAtRef = useRef<number | null>(null);
  const [shouldStartUpload, setShouldStartUpload] = useState(false);
  const [isProcessingMetadata, setIsProcessingMetadata] = useState(false);
  const [rpaDocumentUuid, setRpaDocumentUuid] = useState<string | null>(null);
  const [processingError, setProcessingError] =
    useState<RpaDocumentsSchemaDataItemErrorSummary>(null);

  const {
    files,
    addFiles,
    uploadFiles,
    uploadedFiles,
    uploadStatus,
    isUploading,
  } = useActiveStorageFileUploader();

  const ownerId = Number(cpa_user_id);
  const hasOwner = Number.isFinite(ownerId) && ownerId > 0;

  const { data: rpaDocumentsResponse } = useGetApiV5FirmsRpaDocuments(
    { per_page: 20 },
    {
      query: {
        enabled: Boolean(rpaDocumentUuid) && isActive,
        refetchInterval: (data) => {
          const rpaDocument = data?.data.find(
            (document) => document.uuid === rpaDocumentUuid
          );

          if (rpaDocument?.status === 'failed') {
            return false;
          }

          if (
            rpaDocument?.status === 'completed' &&
            rpaDocument.wf_request?.uuid
          ) {
            return false;
          }

          return POLLING_INTERVAL_MS;
        },
      },
    }
  );

  const rpaDocument = useMemo(
    () =>
      rpaDocumentsResponse?.data.find(
        (document) => document.uuid === rpaDocumentUuid
      ),
    [rpaDocumentUuid, rpaDocumentsResponse?.data]
  );

  const handleDrop = useCallback(
    (fileArray: File[]) => {
      if (!hasOwner) {
        toast.error('Unable to determine the current owner for this upload.');
        return;
      }

      const nextFile = fileArray[0];

      if (!nextFile) {
        return;
      }

      setProcessingError(null);
      setRpaDocumentUuid(null);
      completionFiredRef.current = false;
      workflowRequestWaitStartedAtRef.current = null;

      if (dedupeListRef.current.length >= MAX_FILES) {
        toast.error('Only one tax delivery file can be uploaded here.');
        return;
      }

      const fileWithRpaType = addTaxDeliveryRpaType(nextFile);
      dedupeListRef.current = [fileWithRpaType];
      hasProcessedUploadRef.current = false;
      addFiles([fileWithRpaType]);
      toast.success('1 file added');
      setShouldStartUpload(true);
    },
    [addFiles, hasOwner]
  );

  useEffect(() => {
    if (!shouldStartUpload || files.length === 0 || isUploading) {
      return;
    }

    setShouldStartUpload(false);
    uploadFiles();
  }, [files.length, isUploading, shouldStartUpload, uploadFiles]);

  const uploadFileMetadata = useCallback(
    async (uFiles: (ActiveStorage.Blob | FileError)[]) => {
      setIsProcessingMetadata(true);

      const results = await Promise.allSettled(
        uFiles.map((file) => {
          if (isFileError(file) || !hasOwner) {
            return Promise.reject(isFileError(file) ? file : undefined);
          }

          const payload: WorkflowRPARequestPayload = {
            rpa_type: TAX_DELIVERY_RPA_TYPE,
            cpa_user_id: ownerId,
            signed_blob_id: file.signed_id,
          };

          return apiClient.client.post<WorkflowRPARequestResponse>(
            `${config.api.url}/workflows/rpa_requests`,
            payload
          );
        })
      );

      if (!isActive) {
        return;
      }

      const successCount = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const errorCount = results.filter(
        (result) => result.status === 'rejected'
      ).length;
      const firstFulfilled = results.find(
        (result) => result.status === 'fulfilled'
      );
      const requestUuid =
        firstFulfilled?.status === 'fulfilled'
          ? firstFulfilled.value.data.uuid
          : undefined;

      if (successCount > 0) {
        toast.success(
          `${successCount} file${
            successCount > 1 ? 's' : ''
          } submitted for processing.`
        );
      }

      if (errorCount > 0) {
        const errorMessage = `${errorCount} file${
          errorCount > 1 ? 's' : ''
        } failed to submit for processing`;

        toast.error(errorMessage);

        if (successCount === 0) {
          setProcessingError({
            title: 'Smart Tax Delivery submission failed',
            message: errorMessage,
          });
        }
      }

      setIsProcessingMetadata(false);

      if (successCount > 0) {
        if (requestUuid) {
          processingFileCountRef.current = successCount;
          setRpaDocumentUuid(requestUuid);
        } else {
          setProcessingError({
            title: 'Unable to track processing status',
            message:
              'The uploaded tax return was submitted, but no processing ID was returned.',
          });
        }
      }
    },
    [apiClient.client, hasOwner, isActive, ownerId]
  );

  useEffect(() => {
    if (!isActive || !rpaDocument || completionFiredRef.current) {
      return;
    }

    if (rpaDocument.status === 'completed') {
      const wfRequestUuid = rpaDocument.wf_request?.uuid;

      if (!wfRequestUuid) {
        workflowRequestWaitStartedAtRef.current ??= Date.now();

        if (
          Date.now() - workflowRequestWaitStartedAtRef.current >
          WORKFLOW_REQUEST_WAIT_TIMEOUT_MS
        ) {
          setProcessingError({
            title: 'Unable to open delivery request',
            message:
              'Smart Tax Delivery finished processing, but no workflow request was returned.',
          });
        }

        return;
      }

      completionFiredRef.current = true;
      trackEvent('smart_tax_delivery_completed', {
        source: WHAT_TO_DO_TODAY_SOURCE,
        dashboard_step: WHAT_TO_DO_TODAY_DASHBOARD_STEPS.smartTaxDelivery,
        wf_request_uuid: wfRequestUuid,
        rpa_document_uuid: rpaDocumentUuid,
        file_count: processingFileCountRef.current,
      });
      onComplete({
        wfRequestUuid,
        rpaDocumentUuid,
        fileCount: processingFileCountRef.current,
      });
      navigate(
        `/requests/dispatch/${wfRequestUuid}?comingFrom=${WHAT_TO_DO_TODAY_COMING_FROM}&${WHAT_TO_DO_TODAY_GUIDED_EXPERIENCE_PARAM_V2}=true`
      );
      return;
    }

    if (rpaDocument.status === 'failed') {
      setProcessingError(
        rpaDocument.error_summary ?? {
          title: 'Smart Tax Delivery processing failed',
          message: 'The uploaded tax return could not be processed.',
        }
      );
    }
  }, [
    isActive,
    navigate,
    onComplete,
    rpaDocument,
    rpaDocumentUuid,
    trackEvent,
  ]);

  useEffect(() => {
    if (uploadStatus.success && !hasProcessedUploadRef.current && hasOwner) {
      hasProcessedUploadRef.current = true;
      uploadFileMetadata(uploadedFiles);
    }
  }, [hasOwner, uploadFileMetadata, uploadedFiles, uploadStatus.success]);

  const isUploadInProgress =
    isUploading || isProcessingMetadata || shouldStartUpload;
  const processingStatus: ProcessingStatus = processingError
    ? 'failed'
    : rpaDocumentUuid
    ? rpaDocument?.status === 'completed' && rpaDocument.wf_request?.uuid
      ? 'completed'
      : 'processing'
    : isUploadInProgress
    ? 'uploading'
    : 'idle';

  return {
    handleDrop,
    isUploadInProgress,
    processingStatus,
    processingError,
    hasOwner,
  };
}

interface SmartTaxDeliveryProcessingStatusProps {
  status: Exclude<ProcessingStatus, 'idle'>;
  errorSummary: RpaDocumentsSchemaDataItemErrorSummary;
}

const SmartTaxDeliveryProcessingStatus: React.FC<
  SmartTaxDeliveryProcessingStatusProps
> = ({ status, errorSummary }) => {
  if (status === 'failed') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="warning" sx={{ fontSize: 20 }} />
            <Typography variant="body1" fontWeight={600}>
              {errorSummary?.title ?? 'Smart Tax Delivery processing failed'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {errorSummary?.message ??
              'The uploaded tax return could not be processed.'}
          </Typography>
          {errorSummary?.guidance && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {errorSummary.guidance}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  if (status === 'completed') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success" sx={{ alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="check" sx={{ fontSize: 20 }} />
            <Typography variant="body1" fontWeight={600}>
              Tax return processed successfully!
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Opening the delivery request...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body1" fontWeight={600}>
            {status === 'uploading'
              ? 'Uploading tax return...'
              : 'Processing your tax return...'}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {status === 'uploading'
            ? 'Your file is being uploaded and submitted for processing.'
            : 'This may take a minute.'}
        </Typography>
      </Alert>
    </Box>
  );
};

/**
 * Dashboard "Smart Tax Delivery" modal — a thin dialog hosting the existing
 * Smart Tax Delivery UploadCard and FileList, with owner auto-set to the current
 * user and upload triggered automatically when files are dropped or selected.
 */
interface SmartTaxDeliveryModalContentProps {
  open: boolean;
  onClose: () => void;
  onComplete: (result: SmartTaxDeliveryCompletionResult) => void;
}

const SmartTaxDeliveryModalContent: React.FC<
  SmartTaxDeliveryModalContentProps
> = ({ open, onClose, onComplete }) => {
  const [isLoadingSample, setIsLoadingSample] = useState(false);
  const {
    handleDrop,
    isUploadInProgress,
    processingStatus,
    processingError,
    hasOwner,
  } = useSmartTaxDeliveryUpload(open, onComplete);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSampleSelect = useCallback(
    async (sample: (typeof TAX_DELIVERY_SAMPLES)[number]) => {
      setIsLoadingSample(true);
      try {
        handleDrop([await fetchSamplePdf(sample)]);
      } catch {
        toast.error('Unable to load the selected tax return sample.');
      } finally {
        setIsLoadingSample(false);
      }
    },
    [handleDrop]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <Icon icon="close" fontSize="small" />
        </IconButton>
      </Box>

      {processingStatus === 'idle' ? (
        <Stack spacing={3} sx={{ p: 3, bgcolor: 'background.default' }}>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Try a sample
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {TAX_DELIVERY_SAMPLES.map((sample) => (
                <Button
                  key={sample.filename}
                  variant="outlined"
                  onClick={() => void handleSampleSelect(sample)}
                  disabled={!hasOwner || isUploadInProgress || isLoadingSample}
                >
                  {sample.label}
                </Button>
              ))}
            </Stack>
          </Box>
          <SmartTaxDeliveryUploadCard
            onDrop={handleDrop}
            disabled={!hasOwner || isUploadInProgress}
            multiple={false}
            maxFiles={MAX_FILES}
          />
        </Stack>
      ) : (
        <SmartTaxDeliveryProcessingStatus
          status={processingStatus}
          errorSummary={processingError}
        />
      )}
    </Dialog>
  );
};

export const SmartTaxDeliveryModal: React.FC<SmartTaxDeliveryModalProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  return (
    <SmartTaxDeliveryModalContent
      key={String(open)}
      open={open}
      onClose={onClose}
      onComplete={onComplete}
    />
  );
};
